import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Send } from 'lucide-react';

/**
 * Renders a single guest engagement question (single/multi-choice or text)
 * with skip option.
 */
export default function QuestionPanel({
  question,
  loading,
  selectedOptions,
  textInput,
  setTextInput,
  onOptionSelect,
  onMultiSubmit,
  onTextSubmit,
  onSkip,
}) {
  if (!question) return null;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">{question.question}</p>

      {question.question_type === 'single_choice' && (
        <div className="space-y-2">
          {question.options?.map((option, idx) => (
            <button
              key={`${question.id}-opt-${idx}`}
              onClick={() => onOptionSelect(option, false)}
              className="w-full text-left px-3 py-2 text-sm rounded-lg border hover:bg-primary/5 hover:border-primary transition-colors"
              disabled={loading}
              data-testid={`option-${idx}`}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {question.question_type === 'multi_choice' && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {question.options?.map((option, idx) => (
              <Badge
                key={`${question.id}-multi-${idx}`}
                variant={selectedOptions.includes(option) ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-primary/10"
                onClick={() => onOptionSelect(option, true)}
                data-testid={`multi-option-${idx}`}
              >
                {option}
              </Badge>
            ))}
          </div>
          {selectedOptions.length > 0 && (
            <Button
              size="sm"
              onClick={onMultiSubmit}
              disabled={loading}
              className="w-full mt-2"
            >
              Continue
            </Button>
          )}
        </div>
      )}

      {question.question_type === 'text' && (
        <div className="flex gap-2">
          <Input
            placeholder={question.placeholder || 'Type your answer...'}
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onTextSubmit()}
            className="flex-1 text-sm"
            disabled={loading}
            data-testid="text-input"
          />
          <Button
            size="icon"
            onClick={onTextSubmit}
            disabled={!textInput.trim() || loading}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      )}

      <button
        onClick={onSkip}
        className="text-xs text-muted-foreground hover:text-foreground"
        disabled={loading}
      >
        Skip this question
      </button>
    </div>
  );
}
